/**
 * The settings of an existing line (docs/plans/oncall-line-plan.md 2.3, 4
 * layer 1, 5, 7.5 PATCH /line, 7.7; WP-11), for its admins.
 *
 * Sections: the line (its number read-only, plan 7.7; its name), how it rings
 * (the hunt settings of 2.3 with the live worst-case wait), what callers hear
 * (the three spoken texts), alerts (the no-PHI email recipients, D9) and the
 * fallback Bin's listed phones (4 layer 1: the app cannot read the Bin, so an
 * admin keeps this note in step with it by hand).
 *
 * Save sends only what changed (settingsForm.patchFromForm) and the answer,
 * with the server's clamping applied, becomes the new starting point;
 * Discard goes back to it. Nothing is sent while a field has a message. The
 * Twilio number is shown and never edited here: changing it is a console
 * and runbook step.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { AlertTriangle, BellRing, Loader2, MessageSquareText, PhoneCall, RotateCcw, Shuffle, Siren } from 'lucide-react';
import SegmentedControl from '../../../ui/SegmentedControl';
import { useToast } from '../../../ui/Toast';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage, updateLine } from '../../api/oncallApi';
import type { OncallLine } from '../../api/oncallApi';
import { formatPhone } from '../../lib/format';
import AlertEmailsEditor from './AlertEmailsEditor';
import Toggle from './Toggle';
import WorstCaseWait from './WorstCaseWait';
import { MAX_FALLBACK_NOTE_LENGTH, MAX_TEXT_LENGTH, SETTING_LIMITS } from './huntMath';
import type { IntSettingKey } from './huntMath';
import { formFromLine, parseWhole, patchFromForm, squash, validateForm } from './settingsForm';
import type { FormErrors, SettingsFormState, TextKey } from './settingsForm';

export interface LineSettingsFormProps {
  line: OncallLine;
  /** Active members of the line, for the worst-case wait (null while unknown). */
  activeMembers: number | null;
  onSaved: (line: OncallLine) => void;
}

const cardCls = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';
const labelCls = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';
const hintCls = 'mt-1 text-xs text-gray-500 dark:text-gray-400';
const errorCls = 'mt-1 text-xs text-red-600 dark:text-red-400';

function Section({ id, title, icon: Icon, children }: { id: string; title: string; icon: typeof PhoneCall; children: ReactNode }) {
  return (
    <section className={cardCls} aria-labelledby={`${id}-title`} data-testid={id}>
      <h2 id={`${id}-title`} className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        {title}
      </h2>
      <div className="mt-3 space-y-4">{children}</div>
    </section>
  );
}

const INT_FIELDS: Array<{ key: IntSettingKey; label: string; unit: string; hint: string }> = [
  { key: 'ring_seconds', label: 'Ring time', unit: 's', hint: 'How long each wave of phones rings.' },
  { key: 'wave_size', label: 'Phones at once', unit: '', hint: 'The first to press 1 takes the call; the others stop ringing.' },
  { key: 'max_passes', label: 'Passes through the list', unit: '', hint: 'Full rounds before voicemail.' },
  { key: 'hold_message_every', label: 'Hold message every', unit: 'waves', hint: '"Please continue to hold". 0 turns it off.' },
  { key: 'callback_window_days', label: 'Callback reach', unit: 'days', hint: 'How far back the dial-in callback menu offers callers.' },
];

const TEXT_FIELDS: Array<{ key: TextKey; label: string; hint: string }> = [
  {
    key: 'greeting_text',
    label: 'Greeting',
    hint: 'Said first on every call. If it leaves out the 911 line or the transcription notice, the line adds them.',
  },
  { key: 'hold_text', label: 'Hold message', hint: 'Said between waves while the caller waits.' },
  {
    key: 'voicemail_text',
    label: 'Voicemail prompt',
    hint: 'Said when nobody answered, before the recording. The 911 line is always added.',
  },
];

export default function LineSettingsForm({ line, activeMembers, onSaved }: LineSettingsFormProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<SettingsFormState>(() => formFromLine(line));
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  // Remounts the email editor (its typed draft) on a reset.
  const [version, setVersion] = useState(0);

  // A new line row (loaded, saved or reloaded) is the new starting point.
  useEffect(() => {
    setForm(formFromLine(line));
    setErrors({});
    setFormError(null);
    setEmailDraft('');
    setVersion((v) => v + 1);
  }, [line]);

  const patch = useMemo(() => patchFromForm(line, form), [line, form]);
  const dirty = patch !== null;

  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const setInt = (key: IntSettingKey, value: string) => {
    setForm((prev) => ({ ...prev, ints: { ...prev.ints, [key]: value } }));
    clearError(key);
  };
  const setText = (key: TextKey, value: string) => {
    setForm((prev) => ({ ...prev, texts: { ...prev.texts, [key]: value } }));
    clearError(key);
  };

  const ringSeconds = parseWhole(form.ints.ring_seconds);
  const waveSize = parseWhole(form.ints.wave_size);
  const maxPasses = parseWhole(form.ints.max_passes);
  const inRange = (key: IntSettingKey, n: number | null) =>
    n !== null && n >= SETTING_LIMITS[key].min && n <= SETTING_LIMITS[key].max ? n : null;

  const discard = () => {
    setForm(formFromLine(line));
    setErrors({});
    setFormError(null);
    setEmailDraft('');
    setVersion((v) => v + 1);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const found = validateForm(form);
    if (emailDraft.trim() && !found.alert_emails) {
      found.alert_emails = 'Add the address you typed, or clear it, before saving.';
    }
    setErrors(found);
    if (Object.values(found).some(Boolean)) {
      setFormError('Fix the highlighted fields, then save again.');
      return;
    }
    setFormError(null);
    const body = patchFromForm(line, form);
    if (!body) {
      toast('Nothing has changed.', { type: 'info' });
      return;
    }
    setSaving(true);
    try {
      const saved = await updateLine(body);
      toast('The line\'s settings are saved.', { type: 'success' });
      onSaved(saved);
    } catch (err) {
      setFormError(apiErrorMessage(err, 'The settings could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} noValidate className="space-y-4" data-testid="oncall-settings-form">
      {formError && (
        <p role="alert" data-testid="oncall-settings-error" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {formError}
        </p>
      )}

      <Section id="oncall-settings-line" title="The line" icon={PhoneCall}>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <span className={labelCls}>Number</span>
            <p className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100" data-testid="oncall-settings-number">
              {formatPhone(line.twilio_number_e164)}
            </p>
            <p className={hintCls}>
              Printed on this site&apos;s consent forms. It is set when the line is created; changing it is a runbook step.
            </p>
          </div>
          <div>
            <label htmlFor="oncall-settings-name" className={labelCls}>
              Line name
            </label>
            <input
              id="oncall-settings-name"
              type="text"
              value={form.name}
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({ ...prev, name: value }));
                clearError('name');
              }}
              aria-invalid={errors.name ? true : undefined}
              className={`${fieldCls} w-full`}
            />
            {errors.name && <p className={errorCls}>{errors.name}</p>}
          </div>
        </div>
      </Section>

      <Section id="oncall-settings-hunt" title="How it rings" icon={Shuffle}>
        <WorstCaseWait
          ringSeconds={inRange('ring_seconds', ringSeconds)}
          waveSize={inRange('wave_size', waveSize)}
          maxPasses={inRange('max_passes', maxPasses)}
          activeMembers={activeMembers}
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {INT_FIELDS.map(({ key, label, unit, hint }) => {
            const { min, max } = SETTING_LIMITS[key];
            const id = `oncall-settings-${key}`;
            return (
              <div key={key}>
                <label htmlFor={id} className={labelCls}>
                  {label}
                  {unit ? <span className="font-normal text-gray-500 dark:text-gray-400"> ({unit})</span> : null}
                </label>
                <input
                  id={id}
                  type="number"
                  inputMode="numeric"
                  min={min}
                  max={max}
                  step={1}
                  value={form.ints[key]}
                  onChange={(e) => setInt(key, e.target.value)}
                  aria-invalid={errors[key] ? true : undefined}
                  aria-describedby={`${id}-hint`}
                  data-testid={id}
                  className={`${fieldCls} w-full tabular-nums`}
                />
                {errors[key] ? (
                  <p id={`${id}-hint`} className={errorCls}>
                    {errors[key]}
                  </p>
                ) : (
                  <p id={`${id}-hint`} className={hintCls}>
                    {hint} {min} to {max}.
                  </p>
                )}
              </div>
            );
          })}
          <div>
            <span className={labelCls}>
              Where each call starts
            </span>
            <SegmentedControl
              label="Where each call starts"
              value={form.rotation}
              onChange={(v) => setForm((prev) => ({ ...prev, rotation: v === 'fixed' ? 'fixed' : 'rotate' }))}
              options={[
                { value: 'rotate', label: 'Rotate' },
                { value: 'fixed', label: 'Top of list' },
              ]}
            />
            <p className={hintCls}>
              {form.rotation === 'rotate'
                ? 'Each call starts one person further down, so everyone rings first equally often.'
                : 'Every call starts at the top of the list (priority order).'}
            </p>
          </div>
        </div>

        <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-gray-700 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Voicemail</p>
              <p className={hintCls}>
                {form.bools.voicemail_enabled
                  ? 'When nobody answers, the caller can leave a message, which is transcribed and kept.'
                  : 'Off: when nobody answers, the caller is asked to call back.'}
              </p>
            </div>
            <Toggle
              checked={form.bools.voicemail_enabled}
              onChange={(next) => setForm((prev) => ({ ...prev, bools: { ...prev.bools, voicemail_enabled: next } }))}
              label="Voicemail"
              showState
              testId="oncall-settings-voicemail"
            />
          </div>
          <div className="flex flex-wrap items-start justify-between gap-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Transcription</p>
              <p className={hintCls}>
                {form.bools.transcription_enabled
                  ? 'The conversation and any voicemail are transcribed into the call log. The default greeting says so.'
                  : 'Off: nothing is transcribed, and the default greeting stops saying it is.'}
              </p>
            </div>
            <Toggle
              checked={form.bools.transcription_enabled}
              onChange={(next) => setForm((prev) => ({ ...prev, bools: { ...prev.bools, transcription_enabled: next } }))}
              label="Transcription"
              showState
              testId="oncall-settings-transcription"
            />
          </div>
          <div className="flex flex-wrap items-start justify-between gap-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Full-call recording</p>
              <p className={hintCls}>Not available in this build. Calls are transcribed, not recorded; voicemails are recorded.</p>
            </div>
            <Toggle
              checked={line.settings?.record_calls === true}
              onChange={() => {}}
              label="Full-call recording (not available in this build)"
              disabled
              showState
              testId="oncall-settings-record-calls"
            />
          </div>
        </div>
      </Section>

      <Section id="oncall-settings-texts" title="What callers hear" icon={MessageSquareText}>
        {TEXT_FIELDS.map(({ key, label, hint }) => {
          const id = `oncall-settings-${key}`;
          const length = squash(form.texts[key]).length;
          return (
            <div key={key}>
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {label}
                </label>
                <button
                  type="button"
                  onClick={() => setText(key, '')}
                  data-testid={`${id}-default`}
                  className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0 sm:py-1"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden="true" />
                  Use the default
                </button>
              </div>
              <textarea
                id={id}
                rows={key === 'greeting_text' ? 4 : 3}
                value={form.texts[key]}
                onChange={(e) => setText(key, e.target.value)}
                placeholder="Empty: the default text, naming the site, is used."
                aria-invalid={errors[key] ? true : undefined}
                data-testid={id}
                className={`${fieldCls} w-full`}
              />
              <div className="flex flex-wrap justify-between gap-2">
                <p className={errors[key] ? errorCls : hintCls}>{errors[key] ?? hint}</p>
                <p className={`${hintCls} tabular-nums`}>
                  {length} / {MAX_TEXT_LENGTH}
                </p>
              </div>
            </div>
          );
        })}
        <p className={hintCls}>Spoken by text-to-speech, in English.</p>
      </Section>

      <Section id="oncall-settings-alerts" title="Alert emails" icon={BellRing}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Who is emailed when a call is answered or missed, when the daily heartbeat fails, and when a PIN locks.
        </p>
        <AlertEmailsEditor
          key={version}
          emails={form.alert_emails}
          onChange={(next) => {
            setForm((prev) => ({ ...prev, alert_emails: next }));
            clearError('alert_emails');
          }}
          onDraftChange={(draft) => {
            setEmailDraft(draft);
            if (!draft.trim()) clearError('alert_emails');
          }}
          error={errors.alert_emails}
        />
      </Section>

      <Section id="oncall-settings-fallback" title="Fallback Bin phones" icon={Siren}>
        <div>
          <label htmlFor="oncall-settings-fallback-note" className={labelCls}>
            Who the Twilio fallback Bin rings
          </label>
          <textarea
            id="oncall-settings-fallback-note"
            rows={3}
            value={form.fallback_note}
            onChange={(e) => {
              const value = e.target.value;
              setForm((prev) => ({ ...prev, fallback_note: value }));
              clearError('fallback_note');
            }}
            placeholder="For example: the owner's cell, then the two backups by name"
            aria-invalid={errors.fallback_note ? true : undefined}
            data-testid="oncall-settings-fallback-note"
            className={`${fieldCls} w-full`}
          />
          <p className={errors.fallback_note ? errorCls : hintCls}>
            {errors.fallback_note ??
              `When SiteOps cannot answer, Twilio's fallback Bin rings these backups. SiteOps cannot read the Bin: keep this note in step with it by hand (at most ${MAX_FALLBACK_NOTE_LENGTH} characters). Only line admins see it.`}
          </p>
        </div>
      </Section>

      <div className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 bg-gray-50/95 px-4 py-3 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 sm:mx-0 sm:rounded-xl sm:border">
        <p className="mr-auto text-sm text-gray-600 dark:text-gray-300" aria-live="polite" data-testid="oncall-settings-dirty">
          {dirty ? 'Unsaved changes' : 'All changes saved'}
        </p>
        <button
          type="button"
          onClick={discard}
          disabled={!dirty || saving}
          data-testid="oncall-settings-discard"
          className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          Discard
        </button>
        <button type="submit" disabled={saving} data-testid="oncall-settings-save" className={primaryBtnCls}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save
        </button>
      </div>
    </form>
  );
}
